import { buildItemImageUrl } from '../../OurMenu/ourMenuUtils';
import {
  buildCartMergeKey,
  parseOrderLineAddOns,
} from '../../OurMenu/cartAddOnUtils';
import { getFirstValue } from '../profileFormUtils';
import {
  getCompletePaymentRetryWindowMs,
  getPaymentFailureAt,
} from '../Payment/paymentFailureState';
import { formatDisplayDate, formatDisplayDateTime } from '../../../utils/dateFormat';

const ITEM_NAME_KEYS = [
  'itemName',
  'item_name',
  'name',
  'productName',
  'product_name',
  'menuItemName',
  'menu_item_name',
  'title',
  'mealName',
  'meal_name',
  'product.name',
  'product.title',
  'meal.name',
  'menuItem.name',
  'menu_item.name',
];

/**
 * Coerce API order payloads into a stable list shape. OpenAPI does not document
 * GET /orders/orderPerUser response; we accept common wrappers and field names.
 */

function isPlainObject(v) {
  return v != null && typeof v === 'object' && !Array.isArray(v);
}

/** @param {unknown} payload */
export function extractOrdersArray(payload) {
  if (Array.isArray(payload)) return payload;
  if (!isPlainObject(payload)) return [];

  const tryKeys = ['data', 'orders', 'results', 'items', 'content', 'records'];
  for (const key of tryKeys) {
    const v = payload[key];
    if (Array.isArray(v)) return v;
  }

  const data = payload.data;
  if (isPlainObject(data)) {
    for (const key of tryKeys) {
      const v = data[key];
      if (Array.isArray(v)) return v;
    }
  }

  return [];
}

/** Naive ISO datetime from API (stored as UTC, no offset suffix). */
const ISO_DATETIME_NO_TZ =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d{1,9})?)?$/;

/**
 * @param {unknown} raw
 * @returns {Date | null}
 */
export function parseOrderDate(raw) {
  if (raw == null || raw === '') return null;
  if (raw instanceof Date && !Number.isNaN(raw.getTime())) return raw;
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    const ms = raw > 1e12 ? raw : raw * 1000;
    const d = new Date(ms);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const s = String(raw).trim();
  if (!s) return null;
  const m = s.match(/^(\d{2})-(\d{2})-(\d{4})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?/);
  if (m) {
    const [, dd, mm, yyyy, hh = '0', mi = '0', ss = '0'] = m;
    const d = new Date(
      Date.UTC(
        Number(yyyy),
        Number(mm) - 1,
        Number(dd),
        Number(hh),
        Number(mi),
        Number(ss),
      ),
    );
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (ISO_DATETIME_NO_TZ.test(s)) {
    const d = new Date(`${s}Z`);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const iso = new Date(s);
  return Number.isNaN(iso.getTime()) ? null : iso;
}

/**
 * @param {Record<string, unknown>} row
 * @returns {{ id: string; displayId: string; status: string; orderType: string; total: number | null; placedAt: Date | null; raw: Record<string, unknown> }}
 */
export function normalizeOrderRow(row) {
  const r = isPlainObject(row) ? row : {};
  const idVal = getFirstValue(r, ['id', 'order_id', 'orderId', 'orderID']);
  const numId = idVal !== '' && idVal !== undefined && idVal !== null ? String(idVal) : '';
  const displayId =
    getFirstValue(r, ['order_number', 'orderNumber', 'orderNo', 'reference_id', 'referenceId']) ||
    numId ||
    '—';

  const status = getOrderStatusString(r);

  const orderType = String(
    getFirstValue(r, ['orderType', 'order_type', 'type', 'orderKind']) || '',
  ).trim();

  const totalRaw = getFirstValue(r, [
    'totalAmount',
    'total_amount',
    'total',
    'grandTotal',
    'grand_total',
    'amount',
    'payableAmount',
    'payable_amount',
  ]);
  let total = null;
  if (totalRaw !== '' && totalRaw != null) {
    const n = Number(totalRaw);
    if (Number.isFinite(n)) total = n;
  }
  if (total == null && isPlainObject(r.orderCost)) {
    const oc = r.orderCost;
    const nested = oc.totalAmount ?? oc.total_amount;
    if (nested != null && nested !== '') {
      const n = Number(nested);
      if (Number.isFinite(n)) total = n;
    }
  }

  const placedAt = parseOrderDate(
    getFirstValue(r, [
      'createdAt',
      'created_at',
      'orderDate',
      'order_date',
      'placed_at',
      'placedAt',
      'date',
      'timestamp',
    ]),
  );

  return {
    id: numId || displayId,
    displayId: String(displayId).trim() || '—',
    status,
    orderType,
    total,
    placedAt,
    raw: r,
  };
}

/**
 * Past orders: **Delivered**, **Completed**, and **Cancelled**.
 * @param {ReturnType<typeof normalizeOrderRow>} o
 */
export function orderIsPast(o) {
  const s = normalizeStatusToken(getOrderStatusString(o));
  if (!s) return false;
  if (isDeliveredStatus(s)) return true;
  if (/\bcompleted\b/.test(s)) return true;
  if (/\bcancel(?:led|ed)\b/.test(s) || /\bcanceled\b/.test(s)) return true;
  return false;
}

/** Mirrors client `OrdersListView._isSubscriptionOrder` (ORDSUB / subscription type). */
export function orderIsSubscriptionListItem(o) {
  const num = String(
    (o.raw && o.raw.orderNumber) || o.displayId || '',
  ).toUpperCase();
  const typ = String(o.orderType || '').toLowerCase();
  return num.includes('ORDSUB') || typ === 'subscription';
}

/** @param {ReturnType<typeof normalizeOrderRow>[]} orders */
export function partitionOrdersByLifecycle(orders) {
  const live = [];
  const past = [];
  for (const o of orders) {
    (orderIsPast(o) ? past : live).push(o);
  }
  return {
    live: sortOrdersByDateDesc(live),
    past: sortOrdersByDateDesc(past),
  };
}

/**
 * @param {Record<string, unknown>} raw
 * @param {string} orderTypeFallback
 */
export function extractOrderItemsSummary(raw, orderTypeFallback = '') {
  const r = isPlainObject(raw) ? raw : {};
  const arrays = [
    r.orderItems,
    r.order_items,
    r.items,
    r.lineItems,
    r.line_items,
    r.products,
    r.cartItems,
  ].find((a) => Array.isArray(a));

  const oneLine = (name, qty) => {
    const q = qty != null && String(qty).trim() !== '' ? Number(qty) : 1;
    const n = String(name || '').trim();
    if (!n) return '';
    const qLabel = Number.isFinite(q) && q > 0 ? ` (${q})` : '';
    return `${n}${qLabel}`;
  };

  if (arrays && arrays.length) {
    const parts = [];
    for (const item of arrays) {
      if (!isPlainObject(item)) continue;
      const name = getFirstValue(item, [
        'name',
        'productName',
        'product_name',
        'itemName',
        'item_name',
        'menuItemName',
        'menu_item_name',
        'title',
        'description',
        'mealName',
        'meal_name',
      ]);
      const qty = getFirstValue(item, ['quantity', 'qty', 'count', 'units', 'servings']);
      const line = oneLine(name, qty);
      if (line) parts.push(line);
    }
    if (parts.length) return parts.join(' · ');
  }

  const summary = getFirstValue(r, ['orderSummary', 'summary', 'itemSummary', 'items_text']);
  if (summary) return String(summary).trim();

  return String(orderTypeFallback || '').trim();
}

export function orderLineItemsArray(raw) {
  const r = isPlainObject(raw) ? raw : {};
  const keys = [
    'orderItems',
    'order_items',
    'items',
    'lineItems',
    'line_items',
    'products',
    'cartItems',
  ];
  for (const k of keys) {
    if (Array.isArray(r[k])) return r[k];
  }
  const orderNested = isPlainObject(r.order) ? r.order : null;
  if (orderNested) {
    for (const k of keys) {
      if (Array.isArray(orderNested[k])) return orderNested[k];
    }
  }
  return [];
}

function formatCartPriceDisplay(amount) {
  const n = Number(amount);
  if (!Number.isFinite(n)) return '₹0';
  return `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
}

/**
 * Map order API line items (e.g. orderItems from orderPerUser) to cart rows for reorder.
 * @param {Record<string, unknown>} raw
 */
export function orderLineItemsToCartItems(raw) {
  const rows = orderLineItemsArray(raw);
  /** @type {{ id: number; name: string; price: number; priceDisplay: string; category: string; image: string; quantity: number }[]} */
  const out = [];
  for (const row of rows) {
    if (!isPlainObject(row)) continue;
    const id = Number(getFirstValue(row, ['itemID', 'item_id', 'itemId', 'id']));
    if (!Number.isFinite(id) || id <= 0) continue;
    const name = String(getFirstValue(row, ITEM_NAME_KEYS) || '').trim();
    if (!name) continue;
    const qtyRaw = getFirstValue(row, ['quantity', 'qty', 'count', 'units', 'servings']);
    const q = qtyRaw != null && String(qtyRaw).trim() !== '' ? Number(qtyRaw) : 1;
    const quantity = Number.isFinite(q) && q > 0 ? Math.max(1, Math.round(q)) : 1;
    const price = Number(
      getFirstValue(row, ['price', 'unitPrice', 'unit_price', 'itemPrice', 'item_price', 'amount']) || 0,
    );
    const addOns = parseOrderLineAddOns(row);
    const priceNum = Number.isFinite(price) && price >= 0 ? price : 0;
    const category = String(
      getFirstValue(row, [
        'category',
        'itemCategory',
        'item_category',
        'mealCategory',
        'meal_category',
      ]) || '',
    ).trim();
    out.push({
      id,
      name,
      price: priceNum,
      priceDisplay: formatCartPriceDisplay(priceNum),
      category,
      image: buildItemImageUrl(id),
      quantity,
      addOns,
      cartKey: buildCartMergeKey(id, addOns),
    });
  }
  return out;
}

/** @param {Record<string, unknown>} raw */
export function countOrderLineItems(raw) {
  const items = orderLineItemsArray(raw);
  let n = 0;
  for (const item of items) {
    if (!isPlainObject(item)) continue;
    const qty = getFirstValue(item, ['quantity', 'qty', 'count', 'units', 'servings']);
    const q = qty != null && String(qty).trim() !== '' ? Number(qty) : 1;
    n += Number.isFinite(q) && q > 0 ? q : 1;
  }
  return Math.max(n, items.length ? 1 : 0);
}

/**
 * Bold title line: first line item name (digirasa-client card title pattern).
 * @param {Record<string, unknown>} raw
 * @param {string} orderTypeFallback
 */
export function getPrimaryOrderTitle(raw, orderTypeFallback = '') {
  const items = orderLineItemsArray(raw);
  if (!items.length) {
    return String(orderTypeFallback || '').trim();
  }
  const first = items[0];
  if (!isPlainObject(first)) return String(orderTypeFallback || '').trim();
  const name = getFirstValue(first, ITEM_NAME_KEYS);
  const base = String(name || '').trim();
  if (!base) return String(orderTypeFallback || '').trim();
  if (items.length === 1) return base;
  return `${base} +${items.length - 1} more`;
}

/**
 * Past card subtitle: customizations / notes · item count (see client `PastOrderWidget`).
 * @param {Record<string, unknown>} raw
 */
export function getPastCardSubtitle(raw) {
  const items = orderLineItemsArray(raw);
  if (!items.length) {
    const r = isPlainObject(raw) ? raw : {};
    const summary = getFirstValue(r, ['orderSummary', 'summary', 'itemSummary', 'items_text']);
    return summary ? String(summary).trim() : '';
  }
  const parts = [];
  for (const item of items) {
    if (!isPlainObject(item)) continue;
    const cust = item.customizations;
    if (Array.isArray(cust)) {
      for (const c of cust) {
        if (!isPlainObject(c)) continue;
        const text = getFirstValue(c, ['customizations', 'name', 'label', 'title', 'value']);
        if (text) parts.push(String(text).trim());
      }
    }
    const note = getFirstValue(item, [
      'instructions',
      'notes',
      'specialInstructions',
      'special_instructions',
    ]);
    if (note) parts.push(String(note).trim());
  }
  const globalNote = getFirstValue(isPlainObject(raw) ? raw : {}, [
    'instructions',
    'orderInstructions',
    'order_instructions',
    'specialInstructions',
    'special_instructions',
  ]);
  if (globalNote) parts.push(String(globalNote).trim());

  const uniq = [...new Set(parts.filter(Boolean))];
  const customStr = uniq.join(' · ');
  const n = countOrderLineItems(raw);
  const itemPhrase = n === 1 ? '1 item' : `${n} items`;
  if (customStr) return `${customStr} · ${itemPhrase}`;
  return itemPhrase;
}

/**
 * Checkout “special instructions” stored on the order (`instructions` in admin / kitchen).
 * @param {Record<string, unknown>} raw
 * @returns {string}
 */
export function getOrderSpecialInstructions(raw) {
  if (!isPlainObject(raw)) return '';
  const value = getFirstValue(raw, [
    'instructions',
    'specialInstructions',
    'special_instructions',
    'orderInstructions',
    'order_instructions',
  ]);
  return String(value || '').trim();
}

/**
 * Merchant / branch title for food-app style order cards.
 * @param {Record<string, unknown>} raw
 */
export function getOrderCardBrandName(raw) {
  const r = isPlainObject(raw) ? raw : {};
  const v = getFirstValue(r, [
    'restaurantName',
    'restaurant_name',
    'branchName',
    'branch_name',
    'vendorName',
    'merchantName',
    'kitchenName',
    'storeName',
    'outletName',
    'outlet_name',
    'sellerName',
    'seller_name',
  ]);
  const s = String(v || '').trim();
  return s || 'DigiRasa';
}

/**
 * @param {Record<string, unknown>} raw
 */
export function getOrderCardLocation(raw) {
  const r = isPlainObject(raw) ? raw : {};
  const v = getFirstValue(r, [
    'branchLocation',
    'branch_location',
    'area',
    'locality',
    'city',
    'deliveryAddress',
    'delivery_address',
    'addressLine1',
    'address_line1',
    'address',
  ]);
  return String(v || '').trim();
}

/**
 * HTTPS image for card thumbnail (first line item, then order-level image fields).
 * @param {Record<string, unknown>} raw
 */
export function getOrderCardThumbnailUrl(raw) {
  const items = orderLineItemsArray(raw);
  for (const item of items) {
    if (!isPlainObject(item)) continue;
    const url = getFirstValue(item, [
      'imageUrl',
      'image_url',
      'image',
      'photoUrl',
      'photo_url',
      'thumbnailUrl',
      'thumbnail_url',
      'menuItemImage',
      'menu_item_image',
      'productImage',
      'product_image',
    ]);
    const s = String(url || '').trim();
    if (s && /^https?:\/\//i.test(s)) return s;
  }
  const r = isPlainObject(raw) ? raw : {};
  const top = getFirstValue(r, [
    'restaurantImage',
    'restaurant_image',
    'branchImage',
    'branch_image',
    'imageUrl',
    'image_url',
  ]);
  const t = String(top || '').trim();
  if (t && /^https?:\/\//i.test(t)) return t;
  return '';
}

/**
 * One line: `ORDER #123 | 06/Aug/2026 01:11 PM`
 * @param {string} displayId
 * @param {Date | null} placedAt
 */
export function formatOrderReferenceDateLine(displayId, placedAt) {
  const id = String(displayId || '—').trim();
  let datePart = '';
  if (placedAt && !Number.isNaN(placedAt.getTime())) {
    datePart = formatDisplayDateTime(placedAt);
  }
  if (!datePart) return `ORDER #${id}`;
  return `ORDER #${id} | ${datePart}`;
}

/**
 * Comma-separated line items `Name x 2, Name2 x 1` for order cards (all items, no truncation).
 * @param {Record<string, unknown>} raw
 * @param {number} [maxLen] max length for free-text summary fallback only (not used for structured items)
 */
export function getOrderItemsCommaList(raw, maxLen = 2000) {
  const items = orderLineItemsArray(raw);
  const parts = [];
  for (const item of items) {
    if (!isPlainObject(item)) continue;
    const name = getFirstValue(item, ITEM_NAME_KEYS);
    const qtyRaw = getFirstValue(item, ['quantity', 'qty', 'count', 'units', 'servings']);
    const q = qtyRaw != null && String(qtyRaw).trim() !== '' ? Number(qtyRaw) : 1;
    const n = String(name || '').trim();
    if (!n) continue;
    const qn = Number.isFinite(q) && q > 0 ? Math.round(q) : 1;
    const addOns = parseOrderLineAddOns(item);
    const addonSuffix =
      addOns.length > 0
        ? ` (+ ${addOns.map((a) => a.name).join(', ')})`
        : '';
    parts.push(`${n} x ${qn}${addonSuffix}`);
  }
  if (parts.length) {
    return parts.join(', ');
  }
  const r = isPlainObject(raw) ? raw : {};
  const summary = getFirstValue(r, ['orderSummary', 'summary', 'itemSummary', 'items_text']);
  const s = summary ? String(summary).trim() : '';
  if (!s) return '';
  if (s.length > maxLen) return `${s.slice(0, Math.max(0, maxLen - 1))}…`;
  return s;
}

/**
 * Right-column status copy for past (delivered / cancelled) cards.
 * @param {string} status
 * @param {Date | null} placedAt
 * @param {Record<string, unknown>} raw
 * @returns {{ text: string; kind: 'delivered' | 'cancelled' | 'neutral' }}
 */
export function getPastOrderStatusCaption(status, placedAt, raw) {
  const r = isPlainObject(raw) ? raw : {};
  const whenRaw = getFirstValue(r, [
    'deliveredAt',
    'delivered_at',
    'deliveryTime',
    'delivery_time',
    'completedAt',
    'completed_at',
    'updatedAt',
    'updated_at',
  ]);
  const deliveredAt = parseOrderDate(whenRaw);
  const when = deliveredAt || placedAt;
  const statusToken = normalizeStatusToken(status);
  const vk = getStatusVisualKind(status);

  if (/\bcompleted\b/.test(statusToken)) {
    if (when && !Number.isNaN(when.getTime())) {
      return { text: `Completed on ${formatDisplayDateTime(when)}`, kind: 'delivered' };
    }
    return { text: 'Completed', kind: 'delivered' };
  }

  if (vk === 'delivered') {
    if (when && !Number.isNaN(when.getTime())) {
      return { text: `Delivered on ${formatDisplayDateTime(when)}`, kind: 'delivered' };
    }
    return { text: 'Delivered', kind: 'delivered' };
  }
  if (vk === 'cancelled') {
    if (when && !Number.isNaN(when.getTime())) {
      return { text: `Cancelled on ${formatDisplayDateTime(when)}`, kind: 'cancelled' };
    }
    return { text: formatStatusLabel(status), kind: 'cancelled' };
  }
  return { text: formatStatusLabel(status), kind: 'neutral' };
}

/** @param {Date | null} placedAt */
export function formatPlacedRelativeLine(placedAt) {
  if (!placedAt || Number.isNaN(placedAt.getTime())) return '';
  const startOf = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const today = startOf(new Date());
  const day = startOf(placedAt);
  const diffMs = today.getTime() - day.getTime();
  const diffDays = Math.round(diffMs / 86400000);
  const full = formatDisplayDateTime(placedAt);
  const timeStr = full.includes(' ') ? full.split(' ').slice(-2).join(' ') : '';
  if (diffDays === 0) return `Placed today${timeStr ? `, ${timeStr}` : ''}`;
  if (diffDays === 1) return `Placed yesterday${timeStr ? `, ${timeStr}` : ''}`;
  return `Placed ${full}`;
}

const ORDER_STATUS_FIELD_KEYS = [
  'orderStatus',
  'order_status',
  'status',
  'state',
  'order_state',
];

/**
 * Resolve lifecycle status from a normalized order row or raw API payload.
 * Prefer `orderStatus` (DigiRasa API) over generic `status`.
 * @param {Record<string, unknown> | { raw?: Record<string, unknown>; status?: string } | null | undefined} source
 */
export function getOrderStatusString(source) {
  if (!source) return '';
  const raw = isPlainObject(source.raw) ? source.raw : isPlainObject(source) ? source : {};
  return String(getFirstValue(raw, ORDER_STATUS_FIELD_KEYS) || '').trim();
}

/**
 * Raw lifecycle status from the API for display (no friendly remapping).
 * @param {string | Record<string, unknown> | { raw?: Record<string, unknown>; status?: string } | null | undefined} source
 */
export function getOrderStatusDisplayLabel(source) {
  const status = typeof source === 'string' ? source : getOrderStatusString(source);
  return String(status || '').trim() || '—';
}

function normalizeStatusToken(status) {
  return String(status || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

export function isDeliveredStatus(status) {
  const s = normalizeStatusToken(status);
  return Boolean(s) && /\bdelivered\b/.test(s);
}

export function isPaymentPendingStatus(status) {
  const s = normalizeStatusToken(status);
  // Must match API cancelPendingOrder / initiateRazorpayPayment checks exactly.
  // Do not treat "Pending Partial Payment Approval" (etc.) as unpaid checkout.
  return s === 'payment pending';
}

export function isKitchenAwaitingStatus(status) {
  const s = normalizeStatusToken(status);
  return s === 'pending';
}

/** Kitchen finished; driver has not picked up yet (or is assigned but not collected). */
export function isReadyForDriverPickupStatus(status) {
  const s = normalizeStatusToken(status);
  if (!s) return false;
  if (s === 'ready') return true;
  if (/ready.*dispatch|ready to dispatch/.test(s)) return true;
  if (s.includes('driver accepted')) return true;
  return false;
}

/** Driver has collected the order or it is en route to the customer. */
export function isOrderOnTheWayStatus(status) {
  const s = normalizeStatusToken(status);
  if (!s) return false;
  if (isDeliveredStatus(status)) return false;
  if (isReadyForDriverPickupStatus(status)) return false;
  if (s.includes('driver pickup')) return true;
  if (s.includes('out for')) return true;
  if (/\bon the way\b/.test(s)) return true;
  if (s.includes('transit')) return true;
  if (s.includes('rider')) return true;
  if (s.includes('dispatch') && !s.includes('ready')) return true;
  if (/\bpicked\b/.test(s) && !s.includes('pickup')) return true;
  return false;
}

/**
 * Show "Complete payment" on My Orders for Payment Pending orders within the retry window.
 * Uses failure time when recorded; otherwise order placed time.
 * @param {Record<string, unknown> | { displayId?: string; placedAt?: Date | null; raw?: Record<string, unknown> } | string | null | undefined} order
 * @param {number} [now]
 */
export function shouldShowCompletePayment(order, now = Date.now()) {
  const status = typeof order === 'string' ? order : getOrderStatusString(order);
  if (!isPaymentPendingStatus(status)) return false;

  if (typeof order === 'string' || order == null) return false;

  const windowMs = getCompletePaymentRetryWindowMs();
  const orderNumber = String(
    (typeof order === 'object' && 'displayId' in order ? order.displayId : null) ||
      order.raw?.orderNumber ||
      '',
  ).trim();

  if (orderNumber) {
    const failedAt = getPaymentFailureAt(orderNumber);
    if (failedAt) {
      return now - failedAt <= windowMs;
    }
  }

  const placedAt =
    typeof order === 'object' && 'placedAt' in order ? order.placedAt : null;
  if (!placedAt || Number.isNaN(placedAt.getTime())) return false;

  return now - placedAt.getTime() <= windowMs;
}

/**
 * Milliseconds until Complete payment should hide, or null if not applicable.
 * @param {{ displayId?: string; placedAt?: Date | null; raw?: Record<string, unknown> }} order
 * @param {number} [now]
 * @returns {number | null}
 */
export function getCompletePaymentWindowRemainingMs(order, now = Date.now()) {
  if (!shouldShowCompletePayment(order, now)) return null;

  const windowMs = getCompletePaymentRetryWindowMs();
  const orderNumber = String(order?.displayId || order?.raw?.orderNumber || '').trim();
  const failedAt = orderNumber ? getPaymentFailureAt(orderNumber) : null;
  const anchorMs = failedAt || (order?.placedAt && !Number.isNaN(order.placedAt.getTime())
    ? order.placedAt.getTime()
    : null);
  if (!anchorMs) return null;

  const remaining = anchorMs + windowMs - now;
  return remaining > 0 ? remaining : null;
}

/**
 * Soonest Complete payment expiry among payment-pending live orders.
 * @param {Array<{ displayId?: string; placedAt?: Date | null; raw?: Record<string, unknown> }>} orders
 * @param {number} [now]
 * @returns {number | null}
 */
export function getNearestCompletePaymentWindowExpiryMs(orders, now = Date.now()) {
  let nearest = null;
  for (const order of orders) {
    const remaining = getCompletePaymentWindowRemainingMs(order, now);
    if (remaining == null) continue;
    if (nearest == null || remaining < nearest) nearest = remaining;
  }
  return nearest;
}

/** True when customer can self-cancel an unpaid order from My Orders. */
export function shouldShowCancelPendingOrder(status) {
  return isPaymentPendingStatus(status);
}

/** True when order is out for delivery and a track action is meaningful on the list card. */
export function shouldShowTrackOrderOnList(status) {
  const s = normalizeStatusToken(status);
  if (!s) return false;
  if (isPaymentPendingStatus(status)) return false;
  if (isKitchenAwaitingStatus(status)) return false;
  if (/prepar|packing|cook|making/.test(s)) return false;
  if (isReadyForDriverPickupStatus(status)) return false;
  if (isDeliveredStatus(status)) return false;
  if (/cancel/.test(s)) return false;
  return isOrderOnTheWayStatus(status);
}

const DEFAULT_ORDER_HELP_MAX_AGE_DAYS = 7;

/** Max calendar days after order date that WhatsApp Help is offered (inclusive of order day). */
export function getOrderHelpMaxAgeDays() {
  const raw = process.env.REACT_APP_ORDER_HELP_MAX_AGE_DAYS;
  if (raw == null || String(raw).trim() === '') return DEFAULT_ORDER_HELP_MAX_AGE_DAYS;
  const n = Number.parseInt(String(raw).trim(), 10);
  if (!Number.isFinite(n) || n < 0) return DEFAULT_ORDER_HELP_MAX_AGE_DAYS;
  return n;
}

/** @param {Date} date */
function startOfLocalDay(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

/**
 * True when order is still within the configured Help window (by placed date).
 * @param {Date | null | undefined} placedAt
 */
export function isOrderWithinHelpWindow(placedAt) {
  const maxDays = getOrderHelpMaxAgeDays();
  if (maxDays === 0) return true;
  if (!placedAt || Number.isNaN(placedAt.getTime())) return false;
  const ageDays =
    (startOfLocalDay(new Date()).getTime() - startOfLocalDay(placedAt).getTime()) /
    (24 * 60 * 60 * 1000);
  return ageDays < maxDays;
}

/**
 * Whether Help / Get help should appear for this order (list card or details pane).
 * @param {{ placedAt: Date | null; orderType?: string; displayId?: string; raw?: Record<string, unknown> }} order
 */
export function shouldShowOrderHelp(order) {
  if (!order) return false;
  if (orderIsSubscriptionListItem(order)) return false;
  return isOrderWithinHelpWindow(order.placedAt);
}

export function shouldShowLiveStatusHint(status) {
  return isPaymentPendingStatus(status) || isKitchenAwaitingStatus(status);
}

/** Short pill label for live order header. */
export function getLiveStatusBadgeLabel(status) {
  const s = normalizeStatusToken(status);
  if (!s) return 'In progress';
  if (isDeliveredStatus(status)) return 'Delivered';
  if (isPaymentPendingStatus(status)) return 'Payment Pending';
  if (isKitchenAwaitingStatus(status)) return 'Accepted';
  if (s.includes('preparing') || s === 'packing') return 'Preparing';
  if (isReadyForDriverPickupStatus(status)) {
    if (s.includes('driver accepted')) return 'Driver assigned';
    return 'Ready';
  }
  if (isOrderOnTheWayStatus(status)) return 'On the way';
  return formatStatusLabel(status);
}

/** CSS modifier for live order status pill. */
export function getLivePillClassName(status) {
  const vk = getStatusVisualKind(status);
  if (vk === 'payment-pending') return 'account-orders__pill--pending';
  if (vk === 'kitchen-pending') return 'account-orders__pill--kitchen';
  return 'account-orders__pill--pending';
}

/** @param {ReturnType<typeof normalizeOrderRow>[]} orders */
export function partitionOrdersForMainTab(orders) {
  const filtered = orders.filter((o) => !orderIsSubscriptionListItem(o));
  return partitionOrdersByLifecycle(filtered);
}

export function formatStatusLabel(status) {
  const s = String(status || '').trim();
  if (!s) return 'In progress';
  return s
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .split(/\s+/)
    .map((w) => (w.length ? w[0].toUpperCase() + w.slice(1).toLowerCase() : ''))
    .join(' ')
    .trim();
}

/**
 * Short headline for live orders (mobile-style status copy).
 * @param {string} status
 */
export function getLiveOrderHeadline(status) {
  const s = normalizeStatusToken(status);
  if (isDeliveredStatus(status)) {
    return 'Your order has been delivered.';
  }
  if (isPaymentPendingStatus(status)) {
    return 'Your order is placed but payment is still pending.';
  }
  if (isKitchenAwaitingStatus(status)) {
    return 'Waiting for the kitchen to accept your order.';
  }
  if (/prepar|cook|making/.test(s)) {
    return 'The kitchen is preparing your order.';
  }
  if (isReadyForDriverPickupStatus(status)) {
    if (normalizeStatusToken(status).includes('driver accepted')) {
      return 'A driver has been assigned and is heading to pick up your order.';
    }
    return 'Your order is ready. Waiting for driver pickup.';
  }
  if (isOrderOnTheWayStatus(status)) {
    return 'Your order is on the way.';
  }
  return 'Your order is in progress.';
}

/**
 * @param {string} status
 * @returns {'payment-pending' | 'kitchen-pending' | 'delivered' | 'cancelled' | 'neutral'}
 */
export function getStatusVisualKind(status) {
  const s = normalizeStatusToken(status);
  if (/cancel/.test(s)) return 'cancelled';
  if (isDeliveredStatus(status)) return 'delivered';
  if (isPaymentPendingStatus(status)) return 'payment-pending';
  if (isKitchenAwaitingStatus(status)) return 'kitchen-pending';
  return 'neutral';
}

/** @param {ReturnType<typeof normalizeOrderRow>[]} rows */
export function sortOrdersByDateDesc(rows) {
  return [...rows].sort((a, b) => {
    const ta = a.placedAt ? a.placedAt.getTime() : 0;
    const tb = b.placedAt ? b.placedAt.getTime() : 0;
    if (tb !== ta) return tb - ta;
    return String(b.displayId).localeCompare(String(a.displayId), 'en');
  });
}

/** @param {number | null} amount */
export function formatOrderAmountInr(amount) {
  if (amount == null || !Number.isFinite(amount)) return '';
  return `Rs. ${Number(amount).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
}

/** @param {Date | null} d */
export function formatOrderPlacedAt(d) {
  if (!d) return '';
  return formatDisplayDateTime(d);
}

/** Long weekday + day + month (matches app-style order history headers). */
export function formatOrderCalendarHeading(d) {
  if (!d) return '';
  return formatDisplayDate(d);
}

function toNum(v) {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/**
 * Line items with optional per-line total and subtitle (order detail / client-style list).
 * @param {Record<string, unknown>} raw
 */
export function getOrderDetailLineItems(raw) {
  const items = orderLineItemsArray(raw);
  /** @type {{ name: string; qty: number; lineTotal: number | null; subtitle: string; isVeg: boolean | null }[]} */
  const rows = [];
  for (const item of items) {
    if (!isPlainObject(item)) continue;
    const name = String(getFirstValue(item, ITEM_NAME_KEYS) || '').trim();
    if (!name) continue;
    const qtyRaw = getFirstValue(item, ['quantity', 'qty', 'count', 'units', 'servings']);
    const q = qtyRaw != null && String(qtyRaw).trim() !== '' ? Number(qtyRaw) : 1;
    const qty = Number.isFinite(q) && q > 0 ? Math.round(q) : 1;
    const subtitle = String(
      getFirstValue(item, [
        'unit',
        'size',
        'weight',
        'portion',
        'description',
        'variant',
        'packSize',
        'pack_size',
        'servingSize',
        'serving_size',
      ]) || '',
    ).trim();
    const lineTotal = toNum(
      getFirstValue(item, [
        'lineTotal',
        'line_total',
        'totalPrice',
        'total_price',
        'amount',
        'price',
        'subtotal',
        'itemTotal',
        'item_total',
      ]),
    );
    const vegRaw = getFirstValue(item, ['isVeg', 'is_veg', 'vegetarian', 'veg', 'mealType', 'meal_type']);
    let isVeg = null;
    if (vegRaw != null && vegRaw !== '') {
      const vs = String(vegRaw).toLowerCase();
      if (/non|non-veg|nonveg|chicken|mutton|fish|egg/.test(vs)) isVeg = false;
      else if (/veg|true|1|yes/.test(vs)) isVeg = true;
    }
    const addOns = parseOrderLineAddOns(item);
    rows.push({ name, qty, lineTotal, subtitle, isVeg, addOns });
  }
  return rows;
}

/** Sum of line totals when each line has an amount. */
export function sumOrderLineTotals(lineRows) {
  let s = 0;
  let n = 0;
  for (const row of lineRows) {
    if (row.lineTotal != null) {
      s += row.lineTotal;
      n += 1;
    }
  }
  return n > 0 ? s : null;
}

/** @param {string} s */
function tryParseJsonObjectString(s) {
  const t = String(s || '').trim();
  if (!t || (!t.startsWith('{') && !t.startsWith('['))) return null;
  try {
    const v = JSON.parse(t);
    return isPlainObject(v) ? v : null;
  } catch {
    return null;
  }
}

/**
 * @param {Record<string, unknown>} addr
 * @returns {string[]}
 */
function addressObjectToLines(addr) {
  if (!isPlainObject(addr)) return [];
  const formatted = String(
    getFirstValue(addr, [
      'formattedAddress',
      'formatted_address',
      'fullAddress',
      'full_address',
      'addressString',
      'address_string',
      'completeAddress',
      'complete_address',
      'displayAddress',
      'display_address',
    ]) || '',
  ).trim();
  if (formatted) {
    if (formatted.includes('\n')) {
      return formatted
        .split(/\n+/)
        .map((x) => x.trim())
        .filter(Boolean);
    }
    return [formatted];
  }

  const door = getFirstValue(addr, ['doorNo', 'door_no', 'flatNo', 'flat_no', 'apartment', 'suite', 'unit']);
  const line1 = getFirstValue(addr, [
    'line1',
    'line_1',
    'addressLine1',
    'address_line_1',
    'street',
    'road',
    'address',
    'streetAddress',
    'street_address',
  ]);
  const line2 = getFirstValue(addr, ['line2', 'line_2', 'addressLine2', 'address_line_2', 'landmark', 'area']);
  const locality = getFirstValue(addr, ['locality', 'neighborhood', 'sublocality']);
  const city = getFirstValue(addr, ['city', 'district', 'town']);
  const state = getFirstValue(addr, ['state', 'province', 'region']);
  const pin = getFirstValue(addr, ['pincode', 'pinCode', 'zip', 'postalCode', 'postal_code', 'zipCode']);
  const country = getFirstValue(addr, ['country']);

  const row1 = [door, line1].map((x) => String(x || '').trim()).filter(Boolean).join(', ');
  const row2 = [line2, locality].map((x) => String(x || '').trim()).filter(Boolean).join(', ');
  const row3 = [city, state, pin, country].map((x) => String(x || '').trim()).filter(Boolean).join(', ');

  const lines = [row1, row2, row3].filter((x) => x && x.length);
  if (lines.length) return lines;

  const fallback = String(getFirstValue(addr, ['text', 'value', 'name']) || '').trim();
  return fallback ? [fallback] : [];
}

/**
 * @param {unknown} v
 * @returns {string[] | null}
 */
function deliveryValueToLines(v) {
  if (v == null || v === '') return null;
  if (typeof v === 'string') {
    const t = v.trim();
    if (!t) return null;
    const parsed = tryParseJsonObjectString(t);
    if (parsed) return deliveryValueToLines(parsed);
    return [t];
  }
  if (isPlainObject(v)) {
    const lines = addressObjectToLines(v);
    return lines.length ? lines : null;
  }
  return null;
}

/**
 * DigiRasa `GET /orders/orderPerUser` uses **`orderAddresses`** (plural). Older/alternate keys: `orderAddress`, `orderaddress`, etc.
 * Dot-path lookup is case-sensitive; we normalize keys so plural and casing variants match.
 * @param {Record<string, unknown>} raw
 */
export function getOrderAddressRaw(raw) {
  const r = isPlainObject(raw) ? raw : {};
  for (const k of Object.keys(r)) {
    const norm = String(k).toLowerCase().replace(/[-_\s]/g, '');
    if (norm === 'orderaddress' || norm === 'orderaddresses') return r[k];
  }
  return undefined;
}

const ORDER_ADDRESS_SOURCE_PATHS = [
  'orderAddresses',
  'order_addresses',
  'orderAddress',
  'order_address',
  'deliveryAddress',
  'delivery_address',
  'dropAddress',
  'drop_address',
  'shippingAddress',
  'shipping_address',
  'customerAddress',
  'customer_address',
  'userAddress',
  'user_address',
  'delivery_address_details',
  'customerAddressDetails',
  'customer_address_details',
  'fullAddress',
  'full_address',
  'address',
  'deliveryDetails.address',
  'delivery_details.address',
];

/** @param {Record<string, unknown>} raw */
export function getOrderDeliveryLines(raw) {
  const r = isPlainObject(raw) ? raw : {};

  const orderAddrEarly = getOrderAddressRaw(r);
  const fromOrderAddr = deliveryValueToLines(orderAddrEarly);
  if (fromOrderAddr?.length) return fromOrderAddr;

  for (const path of ORDER_ADDRESS_SOURCE_PATHS) {
    const v = getFirstValue(r, [path]);
    const lines = deliveryValueToLines(v);
    if (lines?.length) return lines;
  }

  const line1 = String(
    getFirstValue(r, [
      'deliveryAddressLine1',
      'delivery_address_line1',
      'shippingAddressLine1',
      'shipping_address_line1',
      'deliveryAddressLine',
      'delivery_address_line',
    ]) || '',
  ).trim();
  const line2 = String(
    getFirstValue(r, ['deliveryAddressLine2', 'delivery_address_line2', 'shippingAddressLine2']) || '',
  ).trim();
  const cityLine = String(
    getFirstValue(r, [
      'deliveryCity',
      'delivery_city',
      'deliveryArea',
      'delivery_area',
      'shippingCity',
      'shipping_city',
    ]) || '',
  ).trim();
  const composed = [line1, line2, cityLine].filter(Boolean);
  if (composed.length) return composed;

  const textBlob = String(
    getFirstValue(r, [
      'deliveryAddressText',
      'delivery_address_text',
      'shipping_address_text',
      'delivery_address_full',
      'full_delivery_address',
    ]) || '',
  ).trim();
  if (textBlob) return [textBlob];

  return [];
}

/** GeoJSON Point / coordinates on nested address objects. */
function coordsFromGeoJson(obj) {
  if (!isPlainObject(obj)) return null;
  const g = obj.geometry || obj.geojson || obj.GeoJSON;
  if (!isPlainObject(g)) return null;
  const c = g.coordinates;
  if (!Array.isArray(c) || c.length < 2) return null;
  if (typeof c[0] === 'number' && typeof c[1] === 'number') {
    const lng = toNum(c[0]);
    const lat = toNum(c[1]);
    if (lat == null || lng == null) return null;
    if (Math.abs(lat) <= 90 && Math.abs(lng) <= 180) return { lat, lng };
  }
  const ring = Array.isArray(c[0]) ? c[0] : null;
  if (Array.isArray(ring) && ring.length >= 2 && typeof ring[0] === 'number') {
    const lng = toNum(ring[0]);
    const lat = toNum(ring[1]);
    if (lat == null || lng == null) return null;
    if (Math.abs(lat) <= 90 && Math.abs(lng) <= 180) return { lat, lng };
  }
  return null;
}

/** @param {Record<string, unknown>} obj */
function coordsFromObject(obj) {
  if (!isPlainObject(obj)) return null;
  const gj = coordsFromGeoJson(obj);
  if (gj) return gj;
  if (Array.isArray(obj.coordinates) && obj.coordinates.length >= 2 && typeof obj.coordinates[0] === 'number') {
    const lng = toNum(obj.coordinates[0]);
    const lat = toNum(obj.coordinates[1]);
    if (lat != null && lng != null && Math.abs(lat) <= 90 && Math.abs(lng) <= 180) return { lat, lng };
  }
  const nested = getFirstValue(obj, ['location', 'geo', 'geoPoint', 'geo_point', 'coordinates']);
  if (isPlainObject(nested)) {
    const c = coordsFromObject(nested);
    if (c) return c;
  }
  if (Array.isArray(nested) && nested.length >= 2) {
    const lng = toNum(nested[0]);
    const lat = toNum(nested[1]);
    if (lat != null && lng != null && Math.abs(lat) <= 90 && Math.abs(lng) <= 180) return { lat, lng };
  }
  const lat = toNum(
    getFirstValue(obj, ['lat', 'latitude', 'Latitude', 'Lat', 'x', 'geo_lat', 'geoLat']),
  );
  const lng = toNum(
    getFirstValue(obj, ['lng', 'lon', 'longitude', 'Longitude', 'Lng', 'y', 'geo_lng', 'geoLng']),
  );
  if (lat == null || lng == null) return null;
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;
  return { lat, lng };
}

/** @param {Record<string, unknown>} obj */
function coordsFromLatLngKeys(obj, latKeys, lngKeys) {
  const lat = toNum(getFirstValue(obj, latKeys));
  const lng = toNum(getFirstValue(obj, lngKeys));
  if (lat == null || lng == null) return null;
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;
  return { lat, lng };
}

/**
 * Pickup / drop coordinates for a map (DigiRasa-style live tracking).
 * @param {Record<string, unknown>} raw
 * @returns {{ pickup: { lat: number; lng: number } | null; drop: { lat: number; lng: number } | null }}
 */
export function getOrderTrackingMapPoints(raw) {
  const r = isPlainObject(raw) ? raw : {};

  const pickupPaths = [
    'pickupLocation',
    'pickup_location',
    'restaurantLocation',
    'restaurant_location',
    'branchLocation',
    'branch.location',
    'kitchenLocation',
    'kitchen.location',
    'vendorLocation',
    'storeLocation',
    'outletLocation',
    'fromLocation',
    'sourceLocation',
    'pickupCoordinates',
    'pickup_coordinates',
    'originLocation',
    'origin_location',
  ];
  const dropPaths = [
    'dropLocation',
    'drop_location',
    'deliveryLocation',
    'delivery_location',
    'customerLocation',
    'customer_location',
    'destinationLocation',
    'destination_location',
    'dropCoordinates',
    'drop_coordinates',
    'toLocation',
    'deliveryCoordinates',
    'delivery_coordinates',
  ];

  let pickup =
    coordsFromLatLngKeys(r, ['pickupLatitude', 'pickup_lat', 'restaurantLatitude', 'branchLatitude', 'kitchenLatitude', 'vendorLatitude', 'sourceLatitude', 'originLatitude'], ['pickupLongitude', 'pickup_lng', 'pickupLon', 'restaurantLongitude', 'branchLongitude', 'kitchenLongitude', 'vendorLongitude', 'sourceLongitude', 'originLongitude']) ||
    null;

  if (!pickup) {
    for (const p of pickupPaths) {
      const v = getFirstValue(r, [p]);
      if (isPlainObject(v)) {
        const c = coordsFromObject(v);
        if (c) {
          pickup = c;
          break;
        }
      }
    }
  }

  if (!pickup) {
    for (const nest of ['branch', 'restaurant', 'outlet', 'kitchen', 'vendor', 'store']) {
      const v = getFirstValue(r, [nest]);
      if (isPlainObject(v)) {
        const c =
          coordsFromObject(v) ||
          coordsFromLatLngKeys(v, ['latitude', 'lat'], ['longitude', 'lng', 'lon']);
        if (c) {
          pickup = c;
          break;
        }
      }
    }
  }

  /** @type {{ lat: number; lng: number } | null} */
  let drop = null;

  const orderAddrRaw = getOrderAddressRaw(r);
  const orderAddrObj =
    isPlainObject(orderAddrRaw) ? orderAddrRaw : tryParseJsonObjectString(String(orderAddrRaw || ''));
  if (isPlainObject(orderAddrObj)) {
    drop =
      coordsFromObject(orderAddrObj) ||
      coordsFromLatLngKeys(orderAddrObj, ['latitude', 'lat', 'Latitude'], ['longitude', 'lng', 'lon', 'Longitude']);
  }

  const addrForCoords = getFirstValue(r, ['deliveryAddress', 'delivery_address', 'shippingAddress', 'dropAddress']);
  const addrObj = isPlainObject(addrForCoords) ? addrForCoords : tryParseJsonObjectString(String(addrForCoords || ''));
  if (!drop && isPlainObject(addrObj)) {
    drop =
      coordsFromObject(addrObj) ||
      coordsFromLatLngKeys(addrObj, ['latitude', 'lat', 'Latitude'], ['longitude', 'lng', 'lon', 'Longitude']);
  }

  const addrAlt = getFirstValue(r, ['userAddress', 'user_address', 'selectedAddress', 'selected_address', 'addressDetails', 'address_details']);
  const addrAltObj = isPlainObject(addrAlt) ? addrAlt : tryParseJsonObjectString(String(addrAlt || ''));
  if (!drop && isPlainObject(addrAltObj)) {
    drop =
      coordsFromObject(addrAltObj) ||
      coordsFromLatLngKeys(addrAltObj, ['latitude', 'lat', 'Latitude'], ['longitude', 'lng', 'lon', 'Longitude']);
  }

  if (!drop) {
    drop =
      coordsFromLatLngKeys(
        r,
        [
          'deliveryLatitude',
          'delivery_lat',
          'dropLatitude',
          'drop_lat',
          'customerLatitude',
          'customer_lat',
          'destLatitude',
          'dest_lat',
        ],
        [
          'deliveryLongitude',
          'delivery_lng',
          'deliveryLon',
          'dropLongitude',
          'drop_lng',
          'customerLongitude',
          'customer_lng',
          'destLongitude',
          'dest_lng',
        ],
      ) || null;
  }

  if (!drop) {
    for (const p of dropPaths) {
      const v = getFirstValue(r, [p]);
      if (isPlainObject(v)) {
        const c = coordsFromObject(v);
        if (c) {
          drop = c;
          break;
        }
      }
    }
  }

  return { pickup, drop };
}

/** @param {Record<string, unknown>} raw */
export function getOrderDeliveryTitle(raw) {
  const r = isPlainObject(raw) ? raw : {};
  let t = getFirstValue(r, ['deliveryLabel', 'addressType', 'address_label', 'addressLabel', 'addressTitle']);
  let s = String(t || '').trim();
  if (!s) {
    const orderAddrRaw = getOrderAddressRaw(r);
    const orderObj = isPlainObject(orderAddrRaw) ? orderAddrRaw : tryParseJsonObjectString(String(orderAddrRaw || ''));
    if (isPlainObject(orderObj)) {
      t = getFirstValue(orderObj, ['addressType', 'type', 'label', 'title', 'name', 'tag']);
      s = String(t || '').trim();
    }
  }
  if (!s) {
    const addr = getFirstValue(r, ['deliveryAddress', 'delivery_address', 'shippingAddress', 'dropAddress']);
    if (isPlainObject(addr)) {
      t = getFirstValue(addr, ['addressType', 'type', 'label', 'title', 'name', 'tag']);
      s = String(t || '').trim();
    }
  }
  if (s) return s;
  return getOrderDeliveryLines(raw).length ? 'Delivery' : '';
}

/** @param {Record<string, unknown>} raw */
export function getOrderDeliveryPartnerLine(raw) {
  const r = isPlainObject(raw) ? raw : {};
  const name = getFirstValue(r, [
    'riderName',
    'rider_name',
    'deliveryPartner',
    'delivery_partner',
    'driverName',
    'driver_name',
    'assignedRider',
    'deliveryExecutive',
    'delivery_executive',
  ]);
  return name ? String(name).trim() : '';
}

/** @param {Record<string, unknown>} raw */
export function isBulkOrPartialOrder(raw) {
  const r = isPlainObject(raw) ? raw : {};
  const oc = isPlainObject(r.orderCost) ? r.orderCost : {};
  return Boolean(r.is_bulk_order || oc.isPartialPayment);
}

/** @param {Record<string, unknown>} raw */
function getOrderPartialPayments(raw) {
  const r = isPlainObject(raw) ? raw : {};
  const oc = isPlainObject(r.orderCost) ? r.orderCost : {};
  const list = Array.isArray(oc.partialPayments) ? oc.partialPayments : [];
  return list.filter((entry) => isPlainObject(entry));
}

/** @param {Record<string, unknown>} raw */
function pickBulkPartialPayment(raw) {
  const payments = getOrderPartialPayments(raw);
  const paid = payments.filter(
    (entry) => String(entry.paymentStatus || entry.payment_status || '').toLowerCase() === 'paid',
  );
  return (
    paid.find((entry) => entry.paymentMethod || entry.payment_method || entry.razorpayPaymentId) ||
    payments.find((entry) => entry.paymentMethod || entry.payment_method || entry.razorpayPaymentId) ||
    paid[0] ||
    payments[0] ||
    null
  );
}

/** @param {string} method */
export function formatPaymentMethodLabel(method) {
  const key = String(method || '')
    .trim()
    .toLowerCase();
  if (!key) return '';
  const labels = {
    upi: 'UPI',
    card: 'Credit & debit card',
    netbanking: 'Net banking',
    wallet: 'Wallet',
    razorpay: 'Razorpay',
    payment_link: 'Payment link',
    cash: 'Cash',
  };
  return labels[key] || formatStatusLabel(method);
}

/** @param {Record<string, unknown>} raw */
export function getOrderPaymentMethod(raw) {
  const r = isPlainObject(raw) ? raw : {};

  if (isBulkOrPartialOrder(r)) {
    const pick = pickBulkPartialPayment(r);
    const method = pick
      ? getFirstValue(pick, ['paymentMethod', 'payment_method', 'paidVia', 'paid_via'])
      : null;
    return method ? formatPaymentMethodLabel(String(method)) : '';
  }

  const top = getFirstValue(r, [
    'paymentMethod',
    'payment_method',
    'paidVia',
    'paid_via',
    'paymentMode',
    'payment_mode',
    'paymentGateway',
    'payment_gateway',
  ]);
  return top ? formatPaymentMethodLabel(String(top)) : '';
}

/** @param {Record<string, unknown>} raw */
export function getOrderRazorpayPaymentId(raw) {
  const r = isPlainObject(raw) ? raw : {};

  if (isBulkOrPartialOrder(r)) {
    const pick = pickBulkPartialPayment(r);
    const ref = pick
      ? getFirstValue(pick, ['razorpayPaymentId', 'razorpay_payment_id', 'paymentId', 'payment_id'])
      : null;
    return ref ? String(ref).trim() : '';
  }

  const top = getFirstValue(r, ['razorpayPaymentId', 'razorpay_payment_id', 'paymentId', 'payment_id']);
  return top ? String(top).trim() : '';
}

/** Default map origin when API does not return kitchen coordinates (single-hub checkout copy). */
export const DEFAULT_ORDER_MAP_ORIGIN_QUERY =
  (typeof process !== 'undefined' && process.env.REACT_APP_ORDER_MAP_ORIGIN_QUERY) ||
  'DigiRasa Kitchen Hub, Miyapur, Hyderabad, Telangana, India';

/** Human-readable destination for map search (delivery lines). */
export function getOrderMapDestQueryText(raw) {
  return getOrderDeliveryLines(raw)
    .map((s) => String(s || '').trim())
    .filter(Boolean)
    .join(', ');
}

/** When pickup coordinates are missing, prefer API text then brand/area. */
export function getOrderMapOriginFallbackText(raw) {
  const r = isPlainObject(raw) ? raw : {};
  const explicit = String(
    getFirstValue(r, [
      'kitchenAddress',
      'kitchen_address',
      'pickupAddress',
      'pickup_address',
      'branchAddress',
      'branch_address',
      'restaurantAddress',
      'restaurant_address',
    ]) || '',
  ).trim();
  if (explicit) return explicit;
  const brand = getOrderCardBrandName(r);
  const loc = getOrderCardLocation(r);
  const parts = [brand, loc].map((x) => String(x || '').trim()).filter(Boolean);
  if (parts.length) return parts.join(', ');
  return DEFAULT_ORDER_MAP_ORIGIN_QUERY;
}

/** Objects that may contain billable amounts (DigiRasa `OrderIn` fields are often on the order root). */
function collectOrderPricingSources(raw) {
  const r = isPlainObject(raw) ? raw : {};
  /** @type {Record<string, unknown>[]} */
  const out = [];
  const push = (v) => {
    if (!isPlainObject(v)) return;
    if (out.includes(v)) return;
    out.push(v);
  };
  if (typeof r.orderCost === 'string') {
    push(tryParseJsonObjectString(r.orderCost));
  }
  push(isPlainObject(r.orderCost) ? r.orderCost : null);
  push(isPlainObject(r.order_cost) ? r.order_cost : null);
  push(isPlainObject(r.pricing) ? r.pricing : null);
  push(isPlainObject(r.priceDetails) ? r.priceDetails : null);
  push(isPlainObject(r.price_details) ? r.price_details : null);
  push(isPlainObject(r.billing) ? r.billing : null);
  push(isPlainObject(r.billSummary) ? r.billSummary : null);
  push(isPlainObject(r.invoice) ? r.invoice : null);
  push(isPlainObject(r.order) ? r.order : null);
  push(isPlainObject(r.data) ? r.data : null);
  push(r);
  return out;
}

/** @param {Record<string, unknown>[]} sources @param {string[]} keys */
function pickMerged(sources, keys) {
  for (const src of sources) {
    const n = toNum(getFirstValue(src, keys));
    if (n != null) return n;
  }
  return null;
}

function humanizeBillLabel(s) {
  const t = String(s || '')
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .trim();
  if (!t) return '';
  return t.charAt(0).toUpperCase() + t.slice(1);
}

/** @param {Record<string, unknown>[]} sources */
function extractBreakdownArrayRows(sources) {
  const arrayKeys = [
    'priceBreakdown',
    'price_breakdown',
    'charges',
    'feeBreakdown',
    'fee_breakdown',
    'billingLines',
    'billing_lines',
    'billing_items',
    'costBreakdown',
    'cost_breakdown',
    'taxBreakdown',
    'tax_breakdown',
  ];
  const labelKeys = [
    'label',
    'title',
    'name',
    'description',
    'key',
    'type',
    'chargeType',
    'charge_type',
    'line',
    'field',
  ];
  const amountKeys = ['amount', 'value', 'price', 'charge', 'total', 'cost', 'fee', 'tax', 'sum'];
  /** @type {{ label: string; amount: number; note?: string }[]} */
  const rows = [];
  for (const src of sources) {
    for (const ak of arrayKeys) {
      const arr = src[ak];
      if (!Array.isArray(arr)) continue;
      for (const entry of arr) {
        if (!isPlainObject(entry)) continue;
        const labelRaw = getFirstValue(entry, labelKeys);
        const label = humanizeBillLabel(String(labelRaw || '').trim());
        const amount = toNum(getFirstValue(entry, amountKeys));
        if (!label || amount == null) continue;
        const noteRaw = getFirstValue(entry, ['note', 'remark']);
        const note = noteRaw ? String(noteRaw).trim() : undefined;
        rows.push({ label, amount, note });
      }
    }
  }
  return rows;
}

/**
 * @param {{ label: string; amount: number | null; note?: string }[]} structured
 * @param {{ label: string; amount: number; note?: string }[]} fromArrays
 */
function mergeBillRowsPreferStructured(structured, fromArrays) {
  if (!fromArrays.length) return structured;
  const labels = new Set(structured.map((r) => r.label.toLowerCase()));
  const extra = [];
  for (const r of fromArrays) {
    const k = r.label.toLowerCase();
    if (labels.has(k)) continue;
    labels.add(k);
    extra.push(r);
  }
  return [...structured, ...extra];
}

/**
 * Bill rows for detail pane — reads **merged** pricing objects (order root + `orderCost`, etc.).
 * Matches DigiRasa `OrderIn` (`totalDeliveryCharge`, `totalPackagingCharge`, `totalTax`, …).
 * @param {Record<string, unknown>} raw
 * @param {number | null} orderTotal from normalizeOrderRow
 */
export function getOrderBillRows(raw, orderTotal) {
  const sources = collectOrderPricingSources(raw);
  const pick = (keys) => pickMerged(sources, keys);

  /** @type {{ label: string; amount: number | null; note?: string }[]} */
  const rows = [];

  const subTotal = pick([
    'subTotal',
    'sub_total',
    'SubTotal',
    'itemsSubtotal',
    'items_subtotal',
    'itemSubtotal',
    'item_subtotal',
    'menuSubtotal',
    'menu_subtotal',
    'subtotal',
    'itemsTotal',
    'itemTotal',
    'item_total',
    'items_total',
    'orderSubtotal',
    'order_subtotal',
    'order_sub_total',
    'itemsAmount',
    'items_amount',
  ]);

  const gstVal = pick([
    'gst',
    'gstAmount',
    'gst_amount',
    'gstTotal',
    'gst_total',
    'totalGst',
    'total_gst',
    'taxGst',
    'tax_gst',
    'orderGst',
    'order_gst',
    'cgstAmount',
    'sgstAmount',
    'igstAmount',
  ]);

  const cgst = pick(['cgst', 'CGST', 'cgstAmount', 'cgst_amount']);
  const sgst = pick(['sgst', 'SGST', 'sgstAmount', 'sgst_amount']);
  const igst = pick(['igst', 'IGST', 'igstAmount', 'igst_amount']);

  const combinedCgstSgst =
    cgst != null && sgst != null ? cgst + sgst : cgst != null ? cgst : sgst != null ? sgst : null;

  const otherTax = pick(['taxes', 'taxAmount', 'tax_amount', 'totalTax', 'total_tax', 'tax', 'vat', 'vatAmount']);

  const delivery = pick([
    'totalDeliveryCharge',
    'total_delivery_charge',
    'deliveryCharge',
    'delivery_charge',
    'deliveryCharges',
    'delivery_charges',
    'deliveryFee',
    'delivery_fee',
    'shippingFee',
    'shipping_fee',
    'shippingCharge',
    'shipping_charge',
    'deliveryCost',
    'delivery_cost',
  ]);

  const packaging = pick([
    'totalPackagingCharge',
    'total_packaging_charge',
    'packagingCharge',
    'packaging_charge',
    'packagingFee',
    'packaging_fee',
    'packingCharge',
    'packing_charge',
    'packingFee',
    'packing_fee',
    'containerCharge',
    'container_charge',
    'packageCharge',
    'package_charge',
  ]);

  const platform = pick([
    'platformFee',
    'platform_fee',
    'platformFeeWithGst',
    'platform_fee_with_gst',
    'serviceFee',
    'service_fee',
    'convenienceFee',
    'convenience_fee',
  ]);

  const surge = pick(['surgeFee', 'surge_fee', 'surgeCharge', 'surge_charge', 'peakFee', 'peak_fee']);

  const handling = pick(['handlingFee', 'handling_fee', 'handlingCharge', 'handling_charge']);

  const discount = pick([
    'discount',
    'discountAmount',
    'discount_amount',
    'couponDiscount',
    'coupon_discount',
    'promoDiscount',
    'totalDiscount',
    'total_discount',
  ]);

  if (subTotal != null) rows.push({ label: 'Sub total', amount: subTotal });

  if (gstVal != null && otherTax != null && Math.abs(gstVal - otherTax) <= 0.02) {
    rows.push({ label: 'GST', amount: gstVal });
  } else if (gstVal != null) {
    rows.push({ label: 'GST', amount: gstVal });
    if (otherTax != null && Math.abs(otherTax - gstVal) > 0.02) {
      rows.push({ label: 'Taxes', amount: otherTax });
    }
  } else if (combinedCgstSgst != null) {
    rows.push({ label: 'GST', amount: combinedCgstSgst });
    if (igst != null && Math.abs(igst) > 0.02) rows.push({ label: 'IGST', amount: igst });
  } else if (otherTax != null) {
    rows.push({ label: 'GST', amount: otherTax });
  } else if (igst != null) {
    rows.push({ label: 'IGST', amount: igst });
  }

  if (delivery != null) {
    rows.push({
      label: 'Delivery charge',
      amount: delivery,
      note: delivery === 0 ? 'FREE' : undefined,
    });
  }

  if (packaging != null) rows.push({ label: 'Packaging charge', amount: packaging });

  if (platform != null) rows.push({ label: 'Platform fee', amount: platform });

  if (surge != null && surge !== 0) rows.push({ label: 'Surge fee', amount: surge });

  if (handling != null && handling !== 0) rows.push({ label: 'Handling fee', amount: handling });

  if (discount != null && discount !== 0) {
    rows.push({ label: 'Discount', amount: -Math.abs(discount) });
  }

  const grand =
    orderTotal ??
    pick([
      'totalAmount',
      'total_amount',
      'grandTotal',
      'grand_total',
      'payableAmount',
      'payable_amount',
      'amount',
      'orderTotal',
      'order_total',
    ]);

  const fromArrays = extractBreakdownArrayRows(sources);
  const rowsOut = mergeBillRowsPreferStructured(rows, fromArrays);

  return { rows: rowsOut, grandTotal: grand };
}

/** @param {ReturnType<typeof normalizeOrderRow> | null} a @param {ReturnType<typeof normalizeOrderRow> | null} b */
export function isSameOrderRow(a, b) {
  if (!a || !b) return false;
  return String(a.id) === String(b.id) && String(a.displayId) === String(b.displayId);
}
